import { Plus } from "@untitledui/icons";
import type { FC, ReactElement, ReactNode } from "react";
import { Button } from "@/components/base/buttons/button";
import { cx } from "@/utils/cx";
import {
	MessageGraphics,
	MessageGraphicsVariant,
	type MessageGraphicsVariant as MessageGraphicsVariantType,
} from "./MessageGraphics";

export const EmptyStateType = {
	Default: "default",
	EmptySearch: "empty-search",
	Map: "map",
} as const;

export type EmptyStateType =
	(typeof EmptyStateType)[keyof typeof EmptyStateType];

const GRAPHIC_BY_TYPE: Record<EmptyStateType, MessageGraphicsVariantType> = {
	[EmptyStateType.Default]: MessageGraphicsVariant.EmptyFolder,
	[EmptyStateType.EmptySearch]: MessageGraphicsVariant.NoSearchResults,
	[EmptyStateType.Map]: MessageGraphicsVariant.Map,
};

export interface EmptyStateProps {
	/**
	 * Visual / contextual variant.
	 * @default "default"
	 */
	type?: EmptyStateType;
	/** Primary heading (e.g. "No Jobs Yet"). */
	title: ReactNode;
	/** Supporting description under the title. */
	description?: ReactNode;
	/**
	 * Whether to show the primary action button.
	 * Defaults to `true` for `default`, `false` for other types.
	 */
	showAction?: boolean;
	/** Action button label. Defaults to "Add". */
	actionLabel?: string;
	/** Called when the action button is pressed. */
	onAction?: () => void;
	/** Custom action slot (overrides default button when set). */
	action?: ReactNode;
	/** Leading icon for the action button. */
	actionIcon?: FC<{ className?: string }>;
	className?: string;
	/** Optional test id override (defaults to `empty-state`). */
	"data-testid"?: string;
}

/**
 * Centered zero-data pattern for lists, tables, and workspace sections.
 *
 * Pair with an error state for failures — not empty data.
 *
 * @see Figma EmptyState 622:6092
 */
export function EmptyState({
	type = EmptyStateType.Default,
	title,
	description,
	showAction,
	actionLabel = "Add",
	onAction,
	action,
	actionIcon: ActionIcon = Plus,
	className,
	"data-testid": dataTestId = "empty-state",
}: EmptyStateProps): ReactElement {
	const shouldShowAction = showAction ?? type === EmptyStateType.Default;

	return (
		<div
			className={cx(
				"mx-auto flex w-full max-w-[500px] min-h-[500px] flex-col items-center justify-center gap-5 px-6 py-12",
				className,
			)}
			data-testid={dataTestId}
			data-type={type}
		>
			<MessageGraphics variant={GRAPHIC_BY_TYPE[type]} />
			<div className="flex w-full flex-col items-center gap-1 overflow-clip text-center">
				<p className="w-full truncate font-display text-md font-extrabold text-primary">
					{title}
				</p>
				{description != null ? (
					<p className="w-full text-sm font-medium text-secondary">
						{description}
					</p>
				) : null}
			</div>
			{shouldShowAction
				? (action ?? (
						<Button
							size="md"
							color="accent"
							iconLeading={ActionIcon}
							onClick={onAction}
						>
							{actionLabel}
						</Button>
					))
				: null}
		</div>
	);
}
